import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Plus, FileDown, Copy, CheckCircle2, Trash2 } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import { formatCurrency, formatDate } from "../lib/format";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import OCRUploadDialog from "../components/OCRUploadDialog";

export default function Invoices() {
  const { t, lang } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [payMethod, setPayMethod] = useState("transfer");

  const load = async () => {
    const [iv, c] = await Promise.all([api.get("/invoices"), api.get("/clients")]);
    setInvoices(iv.data);
    const m = {};
    c.data.forEach((x) => (m[x.id] = x));
    setClientsMap(m);
  };

  useEffect(() => {
    load();
  }, []);

  const downloadPdf = (id, number) => {
    const token = localStorage.getItem("hosys_token");
    const url = `${API}/invoices/${id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener");
  };

  const openPay = (inv) => {
    setPayTarget(inv);
    setPayAmount(inv.total - (inv.paid_amount || 0));
    setPayMethod("transfer");
    setPayOpen(true);
  };

  const submitPay = async () => {
    await api.post(`/invoices/${payTarget.id}/pay`, {
      amount: Number(payAmount),
      method: payMethod,
    });
    toast.success("Pagament registrat");
    setPayOpen(false);
    load();
  };

  const duplicate = async (id) => {
    await api.post(`/invoices/${id}/duplicate`);
    toast.success("Duplicada");
    load();
  };

  const removeInvoice = async (id, number) => {
    if (!window.confirm(
      lang === "es"
        ? `¿Eliminar la factura ${number}? Esta acción no se puede deshacer.`
        : `Eliminar la factura ${number}? Aquesta acció no es pot desfer.`
    )) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(lang === "es" ? "Factura eliminada" : "Factura eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "es" ? "Error al eliminar" : "Error en eliminar"));
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter">{t("invoices.title")}</h1>
          <p className="text-sm text-slate-400 mt-1">{invoices.length} documents</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <OCRUploadDialog kind="invoice" />
          <Link to="/app/invoices/new">
            <Button data-testid="new-invoice-btn" className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400">
              <Plus size={16} className="mr-2" /> {t("invoices.new")}
            </Button>
          </Link>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="card-surface p-12 text-center text-slate-500">{t("invoices.empty")}</div>
      ) : (
        <div className="card-surface overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]" data-testid="invoices-table">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("invoices.number")}</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("invoices.client")}</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("invoices.date")}</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("invoices.due_date")}</th>
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("invoices.total")}</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("invoices.status")}</th>
                {/* Actions column stays visible on landscape mobile by sticking to the right
                    edge. The gradient mask hints at horizontal scroll for the user. */}
                <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium sticky right-0 bg-[#0B0E14]/95 backdrop-blur-sm shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.5)]">Accions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] group">
                  <td className="px-5 py-3 font-medium">
                    <Link to={`/app/invoices/${inv.id}`} className="text-blue-300 hover:text-blue-200" data-testid={`invoice-link-${inv.id}`}>
                      {inv.number}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-slate-300">{clientsMap[inv.client_id]?.name || "—"}</td>
                  <td className="px-5 py-3 text-slate-400">{formatDate(inv.date, lang)}</td>
                  <td className="px-5 py-3 text-slate-400">{inv.due_date ? formatDate(inv.due_date, lang) : "—"}</td>
                  <td className="px-5 py-3 text-right font-medium">
                    {formatCurrency(inv.total, lang)}
                    {inv.paid_amount > 0 && inv.paid_amount < inv.total && (
                      <div className="text-[10px] text-emerald-400">
                        {formatCurrency(inv.paid_amount, lang)} {t("invoices.partial_paid")}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-5 py-3 sticky right-0 bg-[#0B0E14]/95 group-hover:bg-[#10131A]/95 backdrop-blur-sm shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.5)]">
                    <div className="flex items-center justify-end gap-1.5">
                      <button type="button" onClick={() => downloadPdf(inv.id, inv.number)} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-blue-400" data-testid={`invoice-pdf-${inv.id}`}>
                        <FileDown size={14} />
                      </button>
                      <button type="button" onClick={() => duplicate(inv.id)} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-blue-400">
                        <Copy size={14} />
                      </button>
                      {inv.status !== "paid" && (
                        <button type="button" onClick={() => openPay(inv)} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-emerald-400" data-testid={`invoice-pay-${inv.id}`}>
                          <CheckCircle2 size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeInvoice(inv.id, inv.number)}
                        className="p-1.5 rounded hover:bg-rose-500/10 text-slate-400 hover:text-rose-400"
                        data-testid={`invoice-delete-${inv.id}`}
                        title={lang === "es" ? "Eliminar" : "Eliminar"}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="glass border-white/10 max-h-[92vh] overflow-y-auto w-[calc(100vw-1rem)] sm:w-full">
          <DialogHeader>
            <DialogTitle className="font-display">{t("invoices.record_payment")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">{t("invoices.amount")}</label>
              <Input
                type="number"
                step="0.01"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                className="bg-[#05070D] border-white/10"
                data-testid="pay-amount"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">{t("invoices.method")}</label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger className="bg-[#05070D] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B0F19] border-white/10">
                  <SelectItem value="transfer">Transferència</SelectItem>
                  <SelectItem value="card">Targeta</SelectItem>
                  <SelectItem value="cash">Efectiu</SelectItem>
                  <SelectItem value="bizum">Bizum</SelectItem>
                  <SelectItem value="other">Altre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} className="border-white/10">Cancel·lar</Button>
            <Button onClick={submitPay} className="bg-emerald-600 hover:bg-emerald-500" data-testid="pay-submit">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
